import { Injectable } from '@angular/core';
import { Headers, Http, RequestOptions, RequestOptionsArgs, Response } from '@angular/http';
import { AuthHttp } from 'angular2-jwt/angular2-jwt';

import {
    Notification,
    networkNotification,
    serverNotification,
    clientNotification,
    notFoundNotification,
    unknownErrorNotification } from '../entities/entities';

import { Observable } from 'rxjs/Observable';
import { of } from 'rxjs/observable/of';
import 'rxjs/add/observable/throw';
import { map, catchError, tap } from 'rxjs/operators';

import { LoggingService } from './logging.service';

/**
 * An http client that encapsulates the necessary data for communication with the Inmar Intelligence Portal 
 * services. Encapsulates authorization and includes helper methods for ease of working with json.
 *
 */
@Injectable()
export class HttpClientService {

    constructor(
        public authHttp: AuthHttp,
        private loggingService: LoggingService
    ) { }

    getText(url: string): Observable<string> {
        return this.authHttp.get(url).pipe(
            map(this.extractText),
            catchError(this.handleError));
    }

    /**
     * @description Tries to get the resource but doesn't throw an error if doesn't exist (404).
     * @param url url of the resource to try to get.
     */
    tryGet<T>(url: string): Observable<[boolean, T]> {
        // handle this specially b/c of the exception thrown on 404 by angular http client
        return this.authHttp.get(url).pipe(
            // generics and arrow functions don't get along so we have to assert the type of the returned
            map((res) => this.resourceExists(res) as [boolean, T]),
            catchError((err, caught) => {
                return of(this.resourceExists(err) as [boolean, T]);
            }),
        );
    }

    get<T>(url: string): Observable<T> {
        return this.authHttp.get(url).pipe(
            map(this.extractData),
            catchError(this.handleError));
    }

    post<T>(url: string, data: any) {
        return this.authHttp.post(url, data).pipe(
            map(this.extractData),
            catchError(this.handleError));
    }

    put<T>(url: string, data: any) {
        return this.authHttp.put(url, data).pipe(
            map(this.extractData),
            catchError(this.handleError));
    }

    delete<T>(url: string, concurrencyToken: string = '') {
        const args: RequestOptionsArgs = {};
        if (concurrencyToken) {
            const headers = new Headers();
            headers.append('If-Match', concurrencyToken);
            args.headers = headers;
        }
        return this.authHttp.delete(url, args).pipe(
            map(this.extractData),
            catchError(this.handleError));
    }

    private resourceExists<T>(res: Response): [boolean, T] {
        let result: [boolean, T];
        switch (res.status) {
            case 404:
                result = [false, null];
                break;
            case 200:
                result = [true, this.extractData(res)];
                break;
            default:
                this.handleError(res);
        }
        return result;
    }

    private handleExistenceError<T>(res: Response) {
        const result = this.resourceExists<T>(res);
        return of(result);
    }

    private extractText(res: Response): string {
        const data = res.text();
        return data || '';
    }

    private extractData<T>(res: Response): T {
        const data = res.json() as T;
        return data || ({} as T);
    }

    private handleError(error: Response | any) {
        // What should we do with these, try to log them to the server, the console or ?
        let errMsg: string;
        let notification: Notification = unknownErrorNotification;
        if (error instanceof Response) {
            const body = error.text();
            const err = JSON.stringify(body) || '';

            if (error.status === 0) {
                // handle a network error; no network, dns error, server not responding, etc
                errMsg = `${error.status} - ${error.statusText || 'Network Error'}`;
                notification = networkNotification;
            } else if (error.status > 499 && error.status < 600) {
                // 5XX server error
                errMsg = `${error.status} - ${error.statusText || 'Server Error'} ${err}`;
                notification = serverNotification;
            } else if (error.status > 399 && error.status < 500) {
                // 4XX client errors
                errMsg = `${error.status} - ${error.statusText || 'Client Error'} ${err}`;
                if (error.status === 404) {
                    notification = notFoundNotification;
                } else {
                    notification = clientNotification;
                }
            } else {
                // other errors
                errMsg = `${error.status} - ${error.statusText || 'Unknown Error'} ${err}`;
            }
        } else {
            errMsg = error.message ? error.message : error.toString();
        }

        if (this.loggingService) {
            this.loggingService.logError(error, 'http-client.handleError', {
                'errorMessage': errMsg
            });
        }

        // Log the errors to the console for now to make sure we at least have them someplace.
        console.error(errMsg);
        return Observable.throw(notification);
    }
}



// WEBPACK FOOTER //
// D:/a/1/s/src/app/services/http-client.service.ts